/**
 * Laminate CRUD — `laminates/{laminateId}`.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/laminates/laminates.ts
 *   + types.ts
 *
 * Schema parity (verified against mobile types.ts):
 *   - `orgId`, `projectId` — both required (rules gate via them)
 *   - `roomName` — required, drives grouping
 *   - `brand`, `finish` — required
 *   - `laminateCode?`, `edgeBandCode?` — optional product codes
 *   - `photoUrl?`, `photoStoragePath?` — optional swatch photo
 *   - `notes?` — optional free-text
 *   - `createdAt` — server timestamp
 *   - `createdBy` — uid of creator (immutable, mirrors mobile)
 *
 * Same Firestore path as mobile + interior-os backend — mobile sees
 * web edits live and vice-versa.
 */
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Timestamp,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

export type Laminate = {
  id: string;
  projectId: string;
  orgId: string;
  roomName: string;
  brand: string;
  finish: string;
  laminateCode?: string;
  edgeBandCode?: string;
  photoUrl?: string;
  photoStoragePath?: string;
  notes?: string;
  createdAt: Timestamp | null;
  createdBy: string;
};

export type RoomLaminates = {
  roomName: string;
  laminates: Laminate[];
};

export type CreateLaminateInput = {
  projectId: string;
  orgId: string;
  roomName: string;
  brand: string;
  finish: string;
  laminateCode?: string;
  edgeBandCode?: string;
  photoUrl?: string;
  photoStoragePath?: string;
  notes?: string;
  createdBy: string;
};

export async function createLaminate(
  input: CreateLaminateInput,
): Promise<string> {
  const ref = doc(collection(db, 'laminates'));
  const payload: Record<string, unknown> = {
    projectId: input.projectId,
    orgId: input.orgId,
    roomName: input.roomName.trim(),
    brand: input.brand.trim(),
    finish: input.finish.trim(),
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
  };
  if (input.laminateCode?.trim()) payload.laminateCode = input.laminateCode.trim();
  if (input.edgeBandCode?.trim()) payload.edgeBandCode = input.edgeBandCode.trim();
  if (input.photoUrl) payload.photoUrl = input.photoUrl;
  if (input.photoStoragePath) payload.photoStoragePath = input.photoStoragePath;
  if (input.notes?.trim()) payload.notes = input.notes.trim();
  await setDoc(ref, payload);
  return ref.id;
}

export type UpdateLaminateInput = Partial<
  Omit<CreateLaminateInput, 'createdBy' | 'orgId' | 'projectId'>
>;

export async function updateLaminate(
  id: string,
  data: UpdateLaminateInput,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (data.roomName !== undefined) patch.roomName = data.roomName.trim();
  if (data.brand !== undefined) patch.brand = data.brand.trim();
  if (data.finish !== undefined) patch.finish = data.finish.trim();
  // For optional fields: explicit empty string → deleteField() so the
  // Firestore doc shape stays clean. Mirrors mobile semantics.
  if (data.laminateCode !== undefined) {
    patch.laminateCode = data.laminateCode.trim() || deleteField();
  }
  if (data.edgeBandCode !== undefined) {
    patch.edgeBandCode = data.edgeBandCode.trim() || deleteField();
  }
  if (data.photoUrl !== undefined) {
    patch.photoUrl = data.photoUrl || deleteField();
  }
  if (data.photoStoragePath !== undefined) {
    patch.photoStoragePath = data.photoStoragePath || deleteField();
  }
  if (data.notes !== undefined) {
    patch.notes = data.notes.trim() || deleteField();
  }
  await updateDoc(doc(db, 'laminates', id), patch);
}

export async function deleteLaminate(id: string): Promise<void> {
  await deleteDoc(doc(db, 'laminates', id));
}

/** Phosphor icon for the common Indian-residential room names. Used
 *  by the right-rail distribution card; falls back to a folder icon
 *  for anything not in the list. */
export function roomIcon(roomName: string): string {
  const n = roomName.trim().toLowerCase();
  if (n.includes('kitchen')) return 'ph ph-cooking-pot';
  if (n.includes('bath') || n.includes('toilet') || n.includes('wc')) return 'ph ph-bathtub';
  if (n.includes('master bedroom')) return 'ph ph-bed';
  if (n.includes('bedroom')) return 'ph ph-bed';
  if (n.includes('living') || n.includes('hall') || n.includes('lobby')) return 'ph ph-couch';
  if (n.includes('dining')) return 'ph ph-fork-knife';
  if (n.includes('study') || n.includes('office')) return 'ph ph-monitor';
  if (n.includes('pooja') || n.includes('mandir') || n.includes('prayer')) return 'ph ph-spiral';
  if (n.includes('balcony')) return 'ph ph-tree';
  if (n.includes('wardrobe') || n.includes('closet')) return 'ph ph-coat-hanger';
  if (n.includes('store') || n.includes('utility')) return 'ph ph-package';
  if (n.includes('entrance') || n.includes('foyer')) return 'ph ph-door-open';
  if (n.includes('terrace') || n.includes('roof')) return 'ph ph-mountains';
  return 'ph ph-folder-simple';
}

/** Common finish presets — picker chips on the form. Free-text
 *  override always allowed so studios can record custom finishes
 *  ("Soft Touch", "Anti-Fingerprint", etc.). */
export const FINISH_OPTIONS = [
  'MATTE',
  'GLOSS',
  'SUEDE',
  'TEXTURED',
  'SATIN',
  'METALLIC',
  'HIGH GLOSS',
  'WOOD GRAIN',
] as const;

/** Common laminate brands sold in India — autocomplete hints on the
 *  brand field. Order = market-share (Greenlam / Merino /
 *  Century / Sundek / Royale / Virgo are the top 6). */
export const BRAND_HINTS = [
  'Greenlam',
  'Merino',
  'Century',
  'Sundek',
  'Royale Touche',
  'Virgo',
  'Stylam',
  'Aica',
  'Formica',
  'Decolam',
] as const;

/** Curated room presets — verbatim port of the mobile
 *  `COMMON_ROOMS` list (see
 *  `app/(app)/projects/[id]/add-laminate.tsx` line 46). The room
 *  picker shows these below the "PROJECT ROOMS" section so a brand-
 *  new project still has a sensible starter list. */
export const COMMON_ROOMS = [
  'Living Room',
  'Master Bedroom',
  'Bedroom 2',
  'Bedroom 3',
  'Kitchen',
  'Bathroom',
  'Kids Room',
  'Study Room',
  'Dining Room',
  'Hall',
  'Pooja Room',
  'Balcony',
  'Wardrobe',
  'TV Unit',
  'Shoe Rack',
  'Crockery Unit',
] as const;
